import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ShieldCheck, Check, X, Trash2, Home, CalendarPlus, Lock, Unlock, Wallet, Plus, RefreshCw } from 'lucide-react';
import { useAuth, formatApiError } from '../../context/AuthContext';
import PontuacaoTab from './PontuacaoTab';
import WalletsTab from './WalletsTab';
import BetsAdminTab from './BetsAdminTab';
import MonthlyPrizeTab from './MonthlyPrizeTab';
import WithdrawalsTab from './WithdrawalsTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TABS = [
  { key: 'profiles', label: 'Perfis' },
  { key: 'coaches', label: 'Treinadores' },
  { key: 'wallets', label: 'Carteiras' },
  { key: 'withdrawals', label: 'Saques' },
  { key: 'bets', label: 'Apostas' },
  { key: 'monthly', label: 'Prémio Mensal' },
  { key: 'match_days', label: 'Jornadas' },
  { key: 'teams', label: 'Equipas' },
  { key: 'pontuacao', label: 'Pontuação' },
];

/* ==================== PROFILES TAB ==================== */
const ProfilesTab = () => {
  const STATUSES = [
    { key: 'pending', label: 'Pendentes' },
    { key: 'approved', label: 'Aprovados' },
    { key: 'auto', label: 'Auto (Roster)' },
    { key: 'rejected', label: 'Rejeitados' },
  ];
  const [status, setStatus] = useState('pending');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');

  const load = async () => {
    try {
      setLoading(true); setErr('');
      const { data } = await axios.get(`${API}/fantasy/admin/profiles`, { params: { status } });
      setRows(data);
    } catch (ex) { setErr(formatApiError(ex)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const decide = async (row, approve) => {
    if (!window.confirm(approve ? `Aprovar ${row.player_name}?` : `Rejeitar ${row.player_name}?`)) return;
    try {
      await axios.post(`${API}/fantasy/admin/approve-profile`, {
        profile_id: row.id, approve, current_price: row.current_price, position_type: row.position_type,
      });
      await load();
    } catch (ex) { alert(formatApiError(ex)); }
  };
  const remove = async (row) => {
    if (!window.confirm(`Apagar o perfil de ${row.player_name}?`)) return;
    try { await axios.delete(`${API}/fantasy/admin/profiles/${row.id}`); await load(); }
    catch (ex) { alert(formatApiError(ex)); }
  };
  const changePrice = (row, val) => setRows((p) => p.map((r) => r.id === row.id ? { ...r, current_price: Number(val) } : r));
  const changePos = (row, val) => setRows((p) => p.map((r) => r.id === row.id ? { ...r, position_type: val } : r));

  const syncRoster = async () => {
    setSyncing(true); setSyncMsg(''); setErr('');
    try {
      const { data } = await axios.post(`${API}/fantasy/admin/sync-roster`);
      setSyncMsg(data.created > 0
        ? `✅ ${data.created} novos jogadores adicionados ao Fantasy (total activo: ${data.total_active_profiles})`
        : `Já estás em dia — nenhum jogador novo (total activo: ${data.total_active_profiles})`);
      await load();
    } catch (ex) { setErr(formatApiError(ex)); }
    finally { setSyncing(false); }
  };

  const [repricing, setRepricing] = useState(false);
  const repriceBase = async () => {
    if (!window.confirm('Recalcular preços base (6-12€M) para todos os jogadores? Preços já ajustados por jornada não são tocados.')) return;
    setRepricing(true); setSyncMsg(''); setErr('');
    try {
      const { data } = await axios.post(`${API}/fantasy/admin/reprice-base`);
      setSyncMsg(`✅ Preços base recalculados: ${data.updated} atualizados, ${data.skipped} preservados (já tinham preço de jornada)${data.launch_active ? ' — desconto de lançamento aplicado' : ''}`);
      await load();
    } catch (ex) { setErr(formatApiError(ex)); }
    finally { setRepricing(false); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex flex-wrap gap-2 flex-1">
          {STATUSES.map((s) => (
            <button key={s.key} onClick={() => setStatus(s.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${status === s.key ? 'bg-emerald-500 text-slate-900' : 'bg-slate-800/60 text-slate-300 hover:bg-slate-700/60'}`}
              data-testid={`profiles-status-${s.key}`}>{s.label}</button>
          ))}
        </div>
        <button onClick={syncRoster} disabled={syncing}
          className="bg-slate-800 hover:bg-slate-700 border border-emerald-500/30 text-emerald-300 hover:text-emerald-200 text-sm font-black rounded-lg px-3 py-2 inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="sync-roster-btn"
          title="Cria auto-perfis para novos jogadores do plantel">
          <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} /> {syncing ? 'A sincronizar...' : 'Sincronizar Roster'}
        </button>
        <button onClick={repriceBase} disabled={repricing}
          className="bg-slate-800 hover:bg-slate-700 border border-amber-500/30 text-amber-300 hover:text-amber-200 text-sm font-black rounded-lg px-3 py-2 inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="reprice-base-btn"
          title="Recalcula os preços base (6-12€M) a partir das stats dos jogadores">
          <RefreshCw className={`w-4 h-4 ${repricing ? 'animate-spin' : ''}`} /> {repricing ? 'A recalcular...' : 'Preços 6-12€M'}
        </button>
      </div>
      {syncMsg && <div className="text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2 mb-4" data-testid="sync-roster-msg">{syncMsg}</div>}
      {err && <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 mb-4">{err}</div>}
      <div className="bg-slate-900/60 border border-white/10 rounded-2xl overflow-hidden">
        {loading ? <div className="p-6 text-slate-300">A carregar...</div>
          : rows.length === 0 ? <div className="p-6 text-slate-400 text-center">Sem registos.</div>
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-slate-400 uppercase border-b border-white/10">
                  <tr>
                    <th className="px-4 py-3">Utilizador</th>
                    <th className="px-4 py-3">Jogador</th>
                    <th className="px-4 py-3">Posição</th>
                    <th className="px-4 py-3">Preço (€M)</th>
                    <th className="px-4 py-3 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="px-4 py-3">
                        <div className="text-white font-medium">{r.user_name}</div>
                        <div className="text-slate-400 text-xs">{r.user_email}</div>
                      </td>
                      <td className="px-4 py-3 text-white">{r.player_name}</td>
                      <td className="px-4 py-3">
                        <select value={r.position_type} onChange={(e) => changePos(r, e.target.value)}
                          className="bg-slate-800/70 border border-white/10 rounded px-2 py-1 text-white text-xs"
                          disabled={status === 'rejected'}>
                          <option value="LINE">Linha</option>
                          <option value="GK">Guarda-Redes</option>
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <input type="number" step="0.1" min="3" max="15" value={r.current_price}
                          onChange={(e) => changePrice(r, e.target.value)}
                          className="w-20 bg-slate-800/70 border border-white/10 rounded px-2 py-1 text-white text-xs"
                          disabled={status === 'rejected'} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          {status !== 'approved' && (
                            <button onClick={() => decide(r, true)} className="p-2 rounded-lg bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30" title="Aprovar" data-testid={`approve-${r.id}`}>
                              <Check className="w-4 h-4" />
                            </button>
                          )}
                          {status !== 'rejected' && (
                            <button onClick={() => decide(r, false)} className="p-2 rounded-lg bg-amber-500/20 text-amber-300 hover:bg-amber-500/30" title="Rejeitar" data-testid={`reject-${r.id}`}>
                              <X className="w-4 h-4" />
                            </button>
                          )}
                          <button onClick={() => remove(r)} className="p-2 rounded-lg bg-red-500/20 text-red-300 hover:bg-red-500/30" title="Apagar" data-testid={`delete-${r.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
    </div>
  );
};

/* ==================== COACHES TAB ==================== */
const CoachesTab = () => {
  const STATUSES = [
    { key: 'pending', label: 'Pendentes' },
    { key: 'approved', label: 'Aprovados' },
    { key: 'all', label: 'Todos' },
  ];
  const [status, setStatus] = useState('pending');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = async () => {
    try {
      setLoading(true); setErr('');
      const { data } = await axios.get(`${API}/fantasy/admin/coach-requests`, { params: { status } });
      setRows(data);
    } catch (ex) { setErr(formatApiError(ex)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const decide = async (row, approve) => {
    if (!window.confirm(approve ? `Aprovar treinador ${row.name}?` : `Revogar aprovação de ${row.name}?`)) return;
    try {
      await axios.post(`${API}/fantasy/admin/approve-coach`, { user_id: row.id, approve });
      await load();
    } catch (ex) { alert(formatApiError(ex)); }
  };

  const removeCoach = async (row) => {
    if (!window.confirm(`APAGAR o treinador ${row.name} (${row.email}) e todos os dados do Fantasy associados?\n\nIsto não pode ser desfeito.`)) return;
    try {
      await axios.delete(`${API}/fantasy/admin/users/${row.id}`);
      await load();
    } catch (ex) { alert(formatApiError(ex)); }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        {STATUSES.map((s) => (
          <button key={s.key} onClick={() => setStatus(s.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${status === s.key ? 'bg-emerald-500 text-slate-900' : 'bg-slate-800/60 text-slate-300 hover:bg-slate-700/60'}`}
            data-testid={`coaches-status-${s.key}`}>{s.label}</button>
        ))}
      </div>
      {err && <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 mb-4">{err}</div>}
      <div className="bg-slate-900/60 border border-white/10 rounded-2xl overflow-hidden">
        {loading ? <div className="p-6 text-slate-300">A carregar...</div>
          : rows.length === 0 ? <div className="p-6 text-slate-400 text-center">Sem pedidos.</div>
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-slate-400 uppercase border-b border-white/10">
                  <tr>
                    <th className="px-4 py-3">Treinador</th>
                    <th className="px-4 py-3">Equipa</th>
                    <th className="px-4 py-3">Pagamento</th>
                    <th className="px-4 py-3">Estado</th>
                    <th className="px-4 py-3 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`coach-row-${r.id}`}>
                      <td className="px-4 py-3">
                        <div className="text-white font-medium">{r.name}</div>
                        <div className="text-slate-400 text-xs">{r.email}</div>
                      </td>
                      <td className="px-4 py-3 text-white">{r.team_name || <span className="text-slate-500 italic">— sem equipa —</span>}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold border ${r.monthly_paid ? 'bg-emerald-500/20 text-emerald-300 border-emerald-400/30' : 'bg-amber-500/20 text-amber-300 border-amber-400/30'}`}>
                          <Wallet className="w-3 h-3" /> {r.monthly_paid ? 'Paga' : 'Pendente'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold border ${r.coach_approved ? 'bg-emerald-500/20 text-emerald-300 border-emerald-400/30' : 'bg-amber-500/20 text-amber-300 border-amber-400/30'}`}>
                          {r.coach_approved ? 'Aprovado' : 'Aguarda aprovação'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          {!r.coach_approved ? (
                            <button onClick={() => decide(r, true)} className="p-2 rounded-lg bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30" title="Aprovar" data-testid={`approve-coach-${r.id}`}>
                              <Check className="w-4 h-4" />
                            </button>
                          ) : (
                            <button onClick={() => decide(r, false)} className="p-2 rounded-lg bg-amber-500/20 text-amber-300 hover:bg-amber-500/30" title="Revogar aprovação" data-testid={`revoke-coach-${r.id}`}>
                              <X className="w-4 h-4" />
                            </button>
                          )}
                          <button onClick={() => removeCoach(r)} className="p-2 rounded-lg bg-red-500/20 text-red-300 hover:bg-red-500/30" title="Apagar treinador" data-testid={`delete-coach-${r.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
    </div>
  );
};

/* ==================== MATCH DAYS TAB ==================== */
const MatchDaysTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [date, setDate] = useState('');
  const [label, setLabel] = useState('');
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try { setLoading(true); const { data } = await axios.get(`${API}/fantasy/admin/match-days`); setRows(data); }
    catch (ex) { setErr(formatApiError(ex)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault(); setCreating(true); setErr('');
    try {
      await axios.post(`${API}/fantasy/admin/match-days`, { date, label: label || undefined });
      setDate(''); setLabel(''); await load();
    } catch (ex) { setErr(formatApiError(ex)); } finally { setCreating(false); }
  };

  const patch = async (row, updates) => {
    try { await axios.put(`${API}/fantasy/admin/match-days/${row.id}`, updates); await load(); }
    catch (ex) { alert(formatApiError(ex)); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Apagar a jornada "${row.label}"? As escalações associadas serão apagadas.`)) return;
    try { await axios.delete(`${API}/fantasy/admin/match-days/${row.id}`); await load(); }
    catch (ex) { alert(formatApiError(ex)); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-sky-500/10 border border-sky-400/30 rounded-2xl px-4 py-3 text-sm text-sky-100">
        <div className="font-black text-sky-200 mb-1">Fluxo automático das jornadas</div>
        <ul className="text-xs text-sky-100/90 space-y-0.5 list-disc list-inside">
          <li>Mercado e escalações fecham automaticamente ao <b>sábado às 23:59</b> (Lisboa).</li>
          <li>Entre sábado 23:59 e a finalização, os coaches vêem <i>“Resultados sendo processados”</i>.</li>
          <li>Quando lançares os dados no <b>Jogo Ao Vivo</b> e clicares em <b>Finalizar Jornada</b> (aba Pontuação), a próxima jornada abre sozinha para o domingo seguinte.</li>
          <li>Criação manual aqui em baixo só é precisa em casos excepcionais (ex: primeira jornada ou feriado fora do domingo).</li>
        </ul>
      </div>

      <form onSubmit={create} className="bg-slate-900/60 border border-white/10 rounded-2xl p-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-end" data-testid="create-matchday-form">
        <div className="flex-1">
          <label className="text-xs text-slate-400 font-medium">Data da jornada</label>
          <input type="date" required value={date} onChange={(e) => setDate(e.target.value)}
            className="w-full mt-1 bg-slate-800/70 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            data-testid="matchday-date-input" />
        </div>
        <div className="flex-1">
          <label className="text-xs text-slate-400 font-medium">Nome (opcional)</label>
          <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={60}
            placeholder="Ex: Jornada 1 — Lançamento"
            className="w-full mt-1 bg-slate-800/70 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
        </div>
        <button type="submit" disabled={creating || !date}
          className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-slate-900 font-bold rounded-lg px-4 py-2 text-sm inline-flex items-center gap-1.5"
          data-testid="matchday-create-btn">
          <CalendarPlus className="w-4 h-4" /> Criar
        </button>
      </form>

      {err && <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{err}</div>}

      <div className="bg-slate-900/60 border border-white/10 rounded-2xl overflow-hidden">
        {loading ? <div className="p-6 text-slate-300">A carregar...</div>
          : rows.length === 0 ? <div className="p-6 text-slate-400 text-center">Nenhuma jornada criada.</div>
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-slate-400 uppercase border-b border-white/10">
                  <tr>
                    <th className="px-4 py-3">Data</th>
                    <th className="px-4 py-3">Nome</th>
                    <th className="px-4 py-3">Estado</th>
                    <th className="px-4 py-3">Trava manual</th>
                    <th className="px-4 py-3 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="px-4 py-3 text-white font-mono">{r.date}</td>
                      <td className="px-4 py-3 text-white">{r.label}</td>
                      <td className="px-4 py-3">
                        <select value={r.status} onChange={(e) => patch(r, { status: e.target.value })}
                          className="bg-slate-800/70 border border-white/10 rounded px-2 py-1 text-white text-xs">
                          <option value="open">Aberta</option>
                          <option value="closed">Encerrada</option>
                          <option value="processed">Processada</option>
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <button onClick={() => patch(r, { force_locked: !r.force_locked })}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${r.force_locked ? 'bg-red-500/20 text-red-300 border-red-400/30' : 'bg-slate-700/40 text-slate-300 border-white/10'}`}
                          data-testid={`matchday-toggle-lock-${r.id}`}>
                          {r.force_locked ? <><Lock className="w-3.5 h-3.5" /> Fechado</> : <><Unlock className="w-3.5 h-3.5" /> Livre</>}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => remove(r)} className="p-2 rounded-lg bg-red-500/20 text-red-300 hover:bg-red-500/30" title="Apagar">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
    </div>
  );
};

/* ==================== TEAMS TAB ==================== */
const TeamsTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = async () => {
    try { setLoading(true); const { data } = await axios.get(`${API}/fantasy/admin/teams`); setRows(data); }
    catch (ex) { setErr(formatApiError(ex)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const togglePaid = async (row) => {
    try {
      await axios.put(`${API}/fantasy/admin/team-payment`, { team_id: row.id, monthly_paid: !row.monthly_paid });
      await load();
    } catch (ex) { alert(formatApiError(ex)); }
  };

  return (
    <div>
      {err && <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 mb-4">{err}</div>}
      <div className="bg-slate-900/60 border border-white/10 rounded-2xl overflow-hidden">
        {loading ? <div className="p-6 text-slate-300">A carregar...</div>
          : rows.length === 0 ? <div className="p-6 text-slate-400 text-center">Nenhuma equipa criada ainda.</div>
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-slate-400 uppercase border-b border-white/10">
                  <tr>
                    <th className="px-4 py-3">Equipa</th>
                    <th className="px-4 py-3">Treinador</th>
                    <th className="px-4 py-3">Pontos</th>
                    <th className="px-4 py-3">Mensalidade</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="px-4 py-3 text-white font-medium">{r.team_name}</td>
                      <td className="px-4 py-3">
                        <div className="text-white">{r.user_name}</div>
                        <div className="text-slate-400 text-xs">{r.user_email}</div>
                      </td>
                      <td className="px-4 py-3 text-emerald-300 font-bold">{r.total_points.toFixed(1)}</td>
                      <td className="px-4 py-3">
                        <button onClick={() => togglePaid(r)}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${r.monthly_paid ? 'bg-emerald-500/20 text-emerald-300 border-emerald-400/30' : 'bg-amber-500/20 text-amber-300 border-amber-400/30'}`}
                          data-testid={`team-toggle-paid-${r.id}`}>
                          <Wallet className="w-3.5 h-3.5" /> {r.monthly_paid ? 'Paga' : 'Pendente'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
    </div>
  );
};

/* ==================== MAIN ==================== */
const FantasyAdminPage = () => {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState('profiles');

  // Fails-closed guard: only role==='admin' users see the panel. Anyone else (guest, coach,
  // still-loading) gets the restricted screen with their current session state visible.
  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white p-6" data-testid="admin-restricted-screen">
        <div className="text-center max-w-md">
          <ShieldCheck className="w-12 h-12 text-red-400 mx-auto" />
          <h2 className="text-xl font-black mt-3">Acesso restrito ao administrador</h2>
          {user ? (
            <p className="mt-3 text-slate-300 text-sm">
              Estás autenticado como <b className="text-white">{user.email}</b> com papel{' '}
              <b className="text-amber-300">{user.role}</b>. Só a conta de admin pode entrar aqui.
            </p>
          ) : (
            <p className="mt-3 text-slate-300 text-sm">Faz login com a conta de administrador.</p>
          )}
          <div className="flex flex-col gap-2 mt-5">
            {user && (
              <button onClick={logout}
                className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-black rounded-lg py-2.5"
                data-testid="admin-restricted-logout-btn">
                Terminar sessão e entrar como admin
              </button>
            )}
            <Link to="/fantasy" className="text-slate-400 hover:text-white text-sm">← Voltar ao Fantasy</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-emerald-950 to-slate-900">
      <header className="border-b border-white/10 bg-slate-950/60 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/30 shrink-0">
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <div className="text-white font-black leading-tight tracking-tight">RatingPro Fantasy · Admin</div>
              <div className="text-[10px] sm:text-xs text-emerald-300/80 italic leading-tight">
                Sessão: <span className="text-white/90 font-bold" data-testid="admin-session-email">{user.email}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Link to="/fantasy" className="text-slate-300 hover:text-white text-sm inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-white/5">
              <Home className="w-4 h-4" /> Fantasy
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-white mb-6">Painel do Administrador</h1>

        <div className="flex gap-2 mb-6 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-4 py-2 rounded-lg text-sm font-bold whitespace-nowrap ${tab === t.key ? 'bg-emerald-500 text-slate-900' : 'bg-slate-800/60 text-slate-300 hover:bg-slate-700/60'}`}
              data-testid={`admin-tab-${t.key}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'profiles' && <ProfilesTab />}
        {tab === 'coaches' && <CoachesTab />}
        {tab === 'wallets' && <WalletsTab />}
        {tab === 'withdrawals' && <WithdrawalsTab />}
        {tab === 'bets' && <BetsAdminTab />}
        {tab === 'monthly' && <MonthlyPrizeTab />}
        {tab === 'match_days' && <MatchDaysTab />}
        {tab === 'teams' && <TeamsTab />}
        {tab === 'pontuacao' && <PontuacaoTab />}
      </main>
    </div>
  );
};

export default FantasyAdminPage;
